import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Loader2, ArrowRightCircle } from "lucide-react";
import { convertLead } from "../../lib/leads-mod/leads";

const INPUT_CLS =
    "bg-[#0a0a0a] border-white/15 text-white placeholder:text-slate-500 focus-visible:ring-[#1a3fd1]";

// Pull a sensible prefill value out of lead cells by matching column name.
// First case-insensitive substring match wins.
function pickCell(lead, columns, ...needles) {
    if (!lead || !columns) return "";
    const cells = lead.cells || {};
    for (const needle of needles) {
        const col = columns.find((c) =>
            (c.name || "").toLowerCase().includes(needle.toLowerCase()),
        );
        if (col && cells[col.id]) return String(cells[col.id]);
    }
    return "";
}

/**
 * Convert a signed lead into a project. Contained port: the "Convert to
 * Contact" branch from the source dialog is stripped (the contacts bridge
 * will be wired by hand later) — this dialog is convert-to-project only.
 * The project record is written to the module's own lm_projects collection.
 */
export default function ConvertLeadDialog({
    open,
    onOpenChange,
    lead,
    columns,
    onConverted,
}) {
    const [name, setName] = useState("");
    const [location, setLocation] = useState("");
    const [jobId, setJobId] = useState("");
    const [submitting, setSubmitting] = useState(false);

    // Prefill when the dialog opens on a new lead.
    useEffect(() => {
        if (!open || !lead) return;
        const n = pickCell(lead, columns, "name", "customer", "client");
        const addr = pickCell(lead, columns, "address", "location", "site");
        setName(n);
        setLocation(addr);
        setJobId(`PRJ-${new Date().getFullYear()}-${String(Date.now()).slice(-4)}`);
    }, [open, lead, columns]);

    const canSubmit = useMemo(
        () => !!(name.trim() && location.trim() && jobId.trim()),
        [name, location, jobId],
    );

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!canSubmit || !lead) return;
        setSubmitting(true);
        try {
            const payload = {
                name: name.trim(),
                location: location.trim(),
                job_id: jobId.trim(),
            };
            const res = await convertLead(lead.id, payload);
            toast.success(
                res.already_converted
                    ? "Already converted"
                    : "Lead signed · project created",
            );
            onConverted?.(res.project);
        } catch (err) {
            const msg =
                err?.response?.data?.detail || "Couldn't convert this lead.";
            toast.error(msg);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="bg-[#141414] border border-white/15 text-white w-[calc(100vw-1.5rem)] max-w-[520px] max-h-[88vh] overflow-hidden p-4 sm:p-6"
                data-testid="convert-lead-dialog"
            >
                <div className="min-w-0 flex flex-col gap-4 max-h-[calc(88vh-2rem)] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="text-white flex items-center gap-2">
                            <ArrowRightCircle className="w-5 h-5 text-[#8fa4f0]" />
                            Sign lead &amp; create project
                        </DialogTitle>
                        <DialogDescription className="text-slate-400">
                            We'll mint a new project record from this lead. You
                            can edit every field before saving.
                        </DialogDescription>
                    </DialogHeader>

                    <form onSubmit={handleSubmit} className="space-y-3">
                        <div className="space-y-1.5">
                            <Label className="text-slate-300 text-xs uppercase tracking-wider">
                                Customer / project name
                            </Label>
                            <Input
                                data-testid="convert-project-name"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                className={INPUT_CLS}
                                required
                            />
                        </div>
                        <div className="space-y-1.5">
                            <Label className="text-slate-300 text-xs uppercase tracking-wider">
                                Site address
                            </Label>
                            <Input
                                data-testid="convert-project-location"
                                value={location}
                                onChange={(e) => setLocation(e.target.value)}
                                className={INPUT_CLS}
                                required
                            />
                        </div>
                        <div className="space-y-1.5">
                            <Label className="text-slate-300 text-xs uppercase tracking-wider">
                                Project ID
                            </Label>
                            <Input
                                data-testid="convert-project-job-id"
                                value={jobId}
                                onChange={(e) => setJobId(e.target.value)}
                                className={INPUT_CLS}
                                required
                            />
                        </div>

                        <DialogFooter className="gap-2 sm:gap-2 pt-2">
                            <Button
                                type="button"
                                variant="ghost"
                                onClick={() => onOpenChange(false)}
                                data-testid="btn-cancel-convert"
                                className="text-slate-300 hover:text-white hover:bg-white/10"
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                disabled={!canSubmit || submitting}
                                data-testid="btn-submit-convert"
                                className="bg-[#1a3fd1] hover:bg-[#1a3fd1]/80 text-white font-bold disabled:opacity-40 disabled:bg-[#1a1a1a] disabled:text-slate-400"
                            >
                                {submitting ? (
                                    <>
                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                        Converting...
                                    </>
                                ) : (
                                    <>
                                        <ArrowRightCircle className="w-4 h-4 mr-2" />
                                        Sign &amp; create project
                                    </>
                                )}
                            </Button>
                        </DialogFooter>
                    </form>
                </div>
            </DialogContent>
        </Dialog>
    );
}
